import { createContext, useContext, useMemo, useState } from 'react';

import VNModal from './VNModal';

type ModalArgs = {
  title: React.ReactNode;
  size: 'sm' | 'md' | 'lg' | 'xl';
  node: React.ReactNode;
  id: string | null;
};

const defaultModal: ModalArgs = {
  title: 'modal',
  size: 'sm',
  node: null,
  id: null,
};

interface ModalContextProps {
  onOpen: (title: string, size: string, node: React.ReactNode, id?: string) => void;
  onClose: () => void;
  updateTitle: (title: React.ReactNode) => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalContext.Provider');
  }
  return context;
};

export const VNModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [openModal, setOpenModal] = useState(false);
  const [modalArgs, setModalArgs] = useState<ModalArgs>(defaultModal);
  const values = useMemo(
    () => ({
      onOpen: (title: string, size: string, node: React.ReactNode, id: string) => {
        setModalArgs({
          title,
          size,
          node,
          id,
        });
        setOpenModal(true);
      },
      onClose: () => {
        setOpenModal(false);
        setModalArgs(defaultModal);
      },
      updateTitle: (title: React.ReactNode) => {
        setModalArgs((prev) => ({
          ...prev,
          title,
        }));
      },
    }),
    []
  );

  return (
    <ModalContext.Provider value={values}>
      {children}

      <VNModal open={openModal} title={modalArgs.title} size={modalArgs.size} id={modalArgs.id} onClose={values.onClose}>
        {modalArgs.node}
      </VNModal>
    </ModalContext.Provider>
  );
};
